import React, { Fragment, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Chip,
  Collapse
} from '@mui/material';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoIcon from '@mui/icons-material/Info';
import { DeleteForever } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { currencyFormatter } from './type-utilities';
import { formatAccountDisplayName } from '../../utils/scanReportUtils';
import { formatOwnershipAllocation } from '../../utils/formatters';
import { useGetStatementQualityReport, refreshStatementQualityReport, useGetDocumentQualityReport } from '../../api/statementqualityreport.js';
import ExtractedDocumentPanel from './ExtractedDocumentPanel';
import ViewStatementDialog from "./dialog-view-statement-pdf";
import usePrintStatementQualityPDF from './PDFPrintStatementQuality.js';
import { PageHeader } from '../../design-system';
import StatementQualityFilterBar from './report-statement-quality-filter-bar.js';
import {
  applyQualityFilters,
  buildAccountRows,
  collectAccountGroups,
  collectYears,
  describeAccountGroupFilter,
  describeYearFilter,
} from './report-statement-quality-filters.js';
import styles from './report-statement-quality.module.css';
import { orderQualityAccounts } from './report-statement-quality-order.js';
import { PERSONAL_ASSIGNMENT_KEY, ENTITY_ASSIGNMENTS_LABEL } from '../../utils/accountAssignment';

import UserProfileContext from '../../context/userprofilecontext.js';
import { useIsSuperAdmin } from '../../api/swrcurrentusercontext.js';
import PageSkeleton from './pageskeleton.js';
import { orange } from '@mui/material/colors';
import { removeScannedBankStatement, restoreScannedBankStatement, removeStatement } from '../../api/ocrolus_scan.js';
import BankStatementQualityHeatmap from './report-statement-quality-heatmap.js';

const StatementQualityReport = (props) => {
  // const missingStatements = props.missingStatements;
  const statementDataQualityResult = useGetStatementQualityReport();
  const statementDataQualityData = statementDataQualityResult.data;
  const userProfileContext = useContext(UserProfileContext);
  const isSuperAdminResult = useIsSuperAdmin();
  /*
   * Filtering. Account type narrows ROWS (which accounts appear); the year
   * range narrows the month axis (COLUMNS) and, by overlap, which statements
   * are listed. They are kept separate deliberately — see
   * report-statement-quality-filters.js.
   *
   * Everything below must sit above the loading/empty early returns: these
   * are hooks, and usePrintStatementQualityPDF calls hooks of its own.
   */
  const accountRows = useMemo(
    () => buildAccountRows(statementDataQualityData),
    [statementDataQualityData]
  );
  const allYears = useMemo(
    () => collectYears(statementDataQualityData),
    [statementDataQualityData]
  );

  const allGroups = useMemo(
    () => collectAccountGroups(statementDataQualityData),
    [statementDataQualityData]
  );

  const [selectedGroups, setSelectedGroups] = useState(null);
  const [selectedYears, setSelectedYears] = useState(null);
  // Collapse is a reading aid, kept in sync between chart and detail list. It
  // deliberately does NOT reach the PDF — the export follows the filters only.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  // Measured by the chart (it walks up to the scrollport) and shared with
  // the filter bar, so both pin against the same origin.
  const [stickyTop, setStickyTop] = useState(0);

  // Select every year once the data's year span is known, and again only if
  // that span actually changes. Compared as a joined string, not by array
  // identity: collectYears returns a fresh array whenever the payload
  // reference changes, and depending on that identity is the render-loop
  // shape the filter-stability standard exists to prevent.
  const yearSpanRef = useRef(null);
  useEffect(() => {
    const signature = allYears.join(',');
    if (yearSpanRef.current !== signature) {
      yearSpanRef.current = signature;
      setSelectedYears(allYears);
    }
  }, [allYears]);

  const groupSpanRef = useRef(null);
  useEffect(() => {
    const signature = allGroups.join(',');
    if (groupSpanRef.current !== signature) {
      groupSpanRef.current = signature;
      setSelectedGroups(allGroups);
    }
  }, [allGroups]);

  const effectiveYears = selectedYears ?? allYears;
  const effectiveGroups = selectedGroups ?? allGroups;

  const visibleAccountIds = useMemo(
    () => accountRows
      .filter((row) => effectiveGroups.includes(row.accountGroup))
      .map((row) => row.accountId),
    [accountRows, effectiveGroups]
  );

  const visibleData = useMemo(
    () => applyQualityFilters(statementDataQualityData, {
      visibleAccountIds,
      visibleYears: effectiveYears,
    }),
    [statementDataQualityData, visibleAccountIds, effectiveYears]
  );

  const filtersDescription = useMemo(() => [
    ...describeAccountGroupFilter(effectiveGroups, allGroups),
    ...describeYearFilter(effectiveYears, allYears),
  ], [effectiveGroups, allGroups, effectiveYears, allYears]);

  const handleToggleGroup = useCallback((group) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedGroups(allGroups);
    setSelectedYears(allYears);
  }, [allGroups, allYears]);

  // The export follows the screen, and carries the filters that produced it.
  const handlePrint = usePrintStatementQualityPDF(
    visibleData,
    { isDraft: true },
    filtersDescription,
    effectiveYears
  );
  //console.log('isSuperAdminResult');
  //console.log(isSuperAdminResult.data);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [documentToView, setDocumentToView] = useState();
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsDocumentId, setDetailsDocumentId] = useState(null);
  const [detailsSplitDocNumber, setDetailsSplitDocNumber] = useState(null);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [statementToRemove, setStatementToRemove] = useState(null);

  // State for managing collapsed accounts
  const [expandedAccounts, setExpandedAccounts] = useState(new Set());

  const toggleAccountCollapse = (account_id) => {
    setExpandedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(account_id)) {
        newSet.delete(account_id);
      } else {
        newSet.add(account_id);
      }
      return newSet;
    });
  };

  const handleViewDocument = (statement) => {
    const { document_name: documentName, bucket_name: bucketName, object_key: objectKey } = statement;

    // Add validation to ensure we have the required data
    if (!documentName || !bucketName || !objectKey) {
      console.error('Missing required document data:', { documentName, bucketName, objectKey });
      return;
    }

    setDocumentToView({
      bucketName: bucketName,
      objectKey: objectKey,
      documentName: decodeURIComponent(documentName),
    });
    setOpenViewDialog(true);
  };

  const handleViewStatementDetails = (statement) => {
    setDetailsDocumentId(statement.document_id || null);
    setDetailsSplitDocNumber(statement.split_document_number != null ? statement.split_document_number : null);
    setOpenDetailsDialog(true);
  };

  const handleRemoveStatementClick = (statement) => {
    setStatementToRemove(statement);
    setOpenRemoveDialog(true);
  };

  const handleConfirmRemove = () => {
    if (statementToRemove) {
      handleRemoveStatement(statementToRemove);
    }
    setOpenRemoveDialog(false);
    setStatementToRemove(null);
  };

  const handleCancelRemove = () => {
    setOpenRemoveDialog(false);
    setStatementToRemove(null);
  };

  function onCancelView() {
    setOpenViewDialog(false);
  }

  function onCancelDetailsDialog() {
    setOpenDetailsDialog(false);
    setDetailsDocumentId(null);
    setDetailsSplitDocNumber(null);
  }

  async function handleRemoveStatement(statement) {
    console.log(`Removing statement: `);
    console.log(statement);
    if (statement.period_id) {
      // Current pipeline — use statement UUID
      await removeStatement(statement.scan_id, statement.period_id);
    } else {
      // Deprecated pipeline — use pk fields
      await removeScannedBankStatement(statement.scan_id, statement.account_pk, statement.period_pk);
    }
    refreshStatementQualityReport();
  }

  if (!statementDataQualityData || userProfileContext.isLoading || !userProfileContext.userProfile || isSuperAdminResult.isLoading || isSuperAdminResult.error) {
    return (
      <Container maxWidth="xl" sx={{ flexGrow: 1, padding: 2 }}>
        <Paper sx={{ padding: 2, textAlign: 'center', color: 'text.secondary', marginBottom: 2 }}>
          <Typography variant="h4" component="h1">
            Quality & Completeness
          </Typography>
          <PageSkeleton />
        </Paper>
      </Container>
    );
  }

  if (Object.keys(statementDataQualityData).length === 0) {
    return (
      <Container maxWidth="xl" sx={{ flexGrow: 1, padding: 2 }}>
        <Paper sx={{ padding: 2, textAlign: 'center', color: 'text.secondary', marginBottom: 2 }}>
          <Typography variant="h4" component="h1">
            Quality & Completeness
          </Typography>
          <Typography variant="body1" component="p">
            No statements found.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
  {/* Print is a page action, not a filter, so it sits in the page header
          opposite the title — the same slot DRFA uses. Keeping it out of the
          filter bar leaves that bar holding only things that change what the
          table shows. */}
      <Container maxWidth="xl" sx={{ padding: 2, paddingBottom: 0, width: '100%' }}>
        <PageHeader
          title="Quality & Completeness"
          actions={(
            <button type="button" onClick={handlePrint} className={styles.printButton}>
              <PictureAsPdfIcon fontSize="small" />
              Print / Save as PDF
            </button>
          )}
        />
      </Container>

      <BankStatementQualityHeatmap
        toolbar={
          <StatementQualityFilterBar
            accountGroups={allGroups}
            selectedGroups={effectiveGroups}
            onGroupsChange={setSelectedGroups}
            years={allYears}
            selectedYears={effectiveYears}
            onYearsChange={setSelectedYears}
            visibleAccountCount={Object.keys(visibleData).length}
            totalAccountCount={accountRows.length}
            onClear={handleClearFilters}
            onHeightChange={setFilterBarHeight}
            stickyTop={stickyTop}
          />
        }
        data={visibleData}
        visibleYears={effectiveYears}
        collapsedGroups={collapsedGroups}
        onToggleGroup={handleToggleGroup}
        stickyOffset={filterBarHeight}
        onStickyTopChange={setStickyTop}
        // Reaching this component with empty data always means the filters
        // narrowed to zero — the genuinely-no-statements case already
        // returned early above, before this renders at all.
        emptyMessage="No accounts match the current filters."
      />
    <Container
      maxWidth="xl"
      sx={{
        flexGrow: 1,
        padding: 2,
        width: '100%'
      }}
    >
      <Paper sx={{ padding: 2, textAlign: 'center', color: 'text.secondary', marginBottom: 2 }}>
        {/* h2: the page's single h1 is the PageHeader above. */}
        <Typography variant="h4" component="h2">
          Detail
        </Typography>
      </Paper>

      {/* Account Sections — ordered by the backend's canonical account-type
          grouping (sort_index), with a group header wherever the group changes.
          When any account is titled to a business / other entity, accounts are
          first split into Personal and per-entity sections, each with a heading.
          A zero-match state renders inline here rather than replacing the
          whole page, so the filter bar above (and its "Clear filters" button)
          always stays reachable. */}
      {Object.keys(visibleData).length === 0 ? (
        <Paper sx={{ padding: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body1" component="p">
            No accounts match the current filters.
          </Typography>
        </Paper>
      ) : (() => {
        const orderedAccounts = orderQualityAccounts(visibleData);
        let prevGroupKey = null;
        let prevAssignmentKey = null;
        let entitiesHeadingShown = false;
        return orderedAccounts.map(({ accountId: account_id, assignment, group, groupKey }) => {
          const isExpanded = expandedAccounts.has(account_id);
          const accountData = visibleData[account_id];
          const statementCount = accountData.combined_statements.length;
          const missingCount = accountData.combined_statements.filter(stmt => stmt.missing).length;
          const showAssignmentHeading = !!assignment && assignment.key !== prevAssignmentKey;
          prevAssignmentKey = assignment?.key ?? null;
          const showEntitiesHeading = showAssignmentHeading
            && assignment.key !== PERSONAL_ASSIGNMENT_KEY && !entitiesHeadingShown;
          if (showEntitiesHeading) entitiesHeadingShown = true;
          const showGroupHeader = group && groupKey !== prevGroupKey;
          prevGroupKey = groupKey;
          // Shared with the coverage chart above, so the two halves of the
          // report never disagree about what is folded away.
          const groupCollapsed = groupKey ? collapsedGroups.has(groupKey) : false;
          const groupMemberCount = groupKey
            ? orderedAccounts.filter((a) => a.groupKey === groupKey).length
            : 0;

          return (
            <Fragment key={`account-section-${account_id}`}>
              {showEntitiesHeading && (
                <h3 className={styles.entitiesHeading}>{ENTITY_ASSIGNMENTS_LABEL}</h3>
              )}
              {showAssignmentHeading && (
                <h3 className={styles.assignmentHeading}>
                  {assignment.label}
                  {assignment.kind && (
                    <span className={styles.assignmentKind}>{assignment.kind}</span>
                  )}
                </h3>
              )}
              {showGroupHeader && (
                <button
                  type="button"
                  className={styles.groupHeader}
                  aria-expanded={!groupCollapsed}
                  onClick={() => handleToggleGroup(groupKey)}
                  data-testid={`qr-detail-group-${groupKey}`}
                >
                  <span className={styles.groupCaret} aria-hidden="true">
                    {groupCollapsed ? '▶' : '▼'}
                  </span>
                  <span className={styles.groupName}>{group}</span>
                  <span className={styles.groupCount}>
                    {groupCollapsed
                      ? `${groupMemberCount} account${groupMemberCount === 1 ? '' : 's'} hidden`
                      : `${groupMemberCount} account${groupMemberCount === 1 ? '' : 's'}`}
                  </span>
                </button>
              )}
              {!groupCollapsed && (
              <Box sx={{ marginBottom: 3 }}>
            {/* Account Header - Separate from table */}
            <Paper
              sx={{
                padding: 2,
                marginBottom: 0,
                backgroundColor: '#f5f5f5',
                borderBottomLeftRadius: isExpanded ? 0 : 4,
                borderBottomRightRadius: isExpanded ? 0 : 4,
                cursor: 'pointer'
              }}
              onClick={() => toggleAccountCollapse(account_id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  size="small"
                  sx={{ padding: 0 }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>

                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  {formatAccountDisplayName(accountData.bank_account)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={`${statementCount} ${statementCount === 1 ? 'statement' : 'statements'}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {missingCount > 0 && (
                    <Chip
                      label={`${missingCount} missing`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={formatOwnershipAllocation(accountData.title, { abbreviate: true }) || 'Untitled'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Paper>

            {/* Statements Table - Collapsible and Responsive */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <TableContainer
                component={Paper}
                sx={{
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  borderTop: 'none',
                  width: '100%',
                  overflowX: 'auto',
                  // Ensure table takes full width and aligns with heatmap
                  '& .MuiTable-root': {
                    minWidth: '100%',
                    width: '100%'
                  }
                }}
              >
                <Table
                  sx={{
                    minWidth: 800, // Reduced from 1200 to be more reasonable
                    width: '100%',
                    tableLayout: 'auto'
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="left"
                        sx={{
                          minWidth: 100,
                          width: '10%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Actions
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          minWidth: 180,
                          width: '30%'
                        }}
                      >
                        Document
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 80,
                          width: '8%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Start Date
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 80,
                          width: '8%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        End Date
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 100,
                          width: '11%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Starting Balance
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 100,
                          width: '11%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Ending Balance
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 110,
                          width: '11%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Expected Transaction Total
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          minWidth: 110,
                          width: '11%',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Actual Transaction Total
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {accountData.combined_statements.map((statement, index) => {
                      let matching_amounts = true;
                      if (index > 0 && !statement.missing && !accountData.combined_statements[index - 1].missing) {
                        const prevEnd = accountData.combined_statements[index - 1].end_balance;
                        const currBegin = statement.begin_balance;
                        // Only assert balance continuity when both values are
                        // present; a null balance (allowed on investment
                        // statements) is a data gap, not a mismatch.
                        matching_amounts = (prevEnd == null || currBegin == null) ? true : prevEnd === currBegin;
                      }

                      // balance_match_to_transaction is null ("not applicable")
                      // for investment accounts and when a balance is missing —
                      // render those columns neutral with N/A rather than red.
                      const reconApplicable = statement.balance_match_to_transaction === true || statement.balance_match_to_transaction === false;
                      const reconColor = !reconApplicable
                        ? 'text.secondary'
                        : (statement.balance_match_to_transaction ? 'success.main' : 'error.main');

                      return (
                        <TableRow key={`statement-${account_id}-${index}`}>
                          {statement.missing ? (
                            <Fragment>
                              <TableCell align="left">
                                {/* No actions for missing statements */}
                              </TableCell>
                              <TableCell align="left" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                MISSING
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {statement.expected_begin_date}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {statement.expected_end_date}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>-</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>-</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>-</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>-</TableCell>
                            </Fragment>
                          ) : (
                            <Fragment>
                              <TableCell align="left">
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleViewDocument(statement)}
                                    title={statement.document_name ? `View Document ${decodeURIComponent(statement.document_name)}` : 'View Document'}
                                    size="small"
                                    disabled={!statement.document_name || !statement.bucket_name || !statement.object_key}
                                  >
                                    <PictureAsPdfIcon />
                                  </IconButton>
                                  <IconButton
                                    color="secondary"
                                    onClick={() => handleViewStatementDetails(statement)}
                                    title="View Statement Details"
                                    size="small"
                                  >
                                    <InfoIcon />
                                  </IconButton>
                                  {isSuperAdminResult.data &&
                                    <IconButton
                                      color="error"
                                      onClick={() => handleRemoveStatementClick(statement)}
                                      title="Remove Statement"
                                      size="small"
                                    >
                                      <DeleteForever />
                                    </IconButton>
                                  }
                                </Box>
                              </TableCell>
                              <TableCell
                                align="left"
                                sx={{
                                  color: statement.status === 'OVERLAP' ? orange[900] : 'text.primary',
                                  fontFamily: statement.document_name ? 'inherit' : 'italic',
                                  wordBreak: 'break-word',
                                  maxWidth: 200
                                }}
                              >
                                {statement.document_name ? decodeURIComponent(statement.document_name) : 'No Document'}
                                {statement.has_transactions_outside_period && (
                                  <Chip
                                    label={`${statement.transactions_outside_period_count} txn(s) outside period`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.65rem' }}
                                  />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {statement.begin_date}
                                {statement.begin_date_inferred && (
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ color: 'warning.main', ml: 0.25 }}
                                    title="Start date was estimated (not explicitly stated on the statement)"
                                  >
                                    *
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">{statement.end_date}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: matching_amounts ? 'success.main' : 'error.main',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {currencyFormatter(statement.begin_balance)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {currencyFormatter(statement.end_balance)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: reconColor,
                                  whiteSpace: 'nowrap'
                                }}
                                title={!reconApplicable ? 'Balance-to-transaction reconciliation does not apply to investment accounts (balances move with the market)' : undefined}
                              >
                                {reconApplicable ? currencyFormatter(statement.expected_transaction_total) : 'N/A'}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: reconColor,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {reconApplicable ? currencyFormatter(statement.transaction_total) : 'N/A'}
                              </TableCell>
                            </Fragment>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
              </Box>
              )}
            </Fragment>
          );
        });
      })()}

      <Box sx={{ mt: 1, px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <Typography component="span" variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>*</Typography>
          {' '}Start date was estimated (not explicitly stated on the statement)
        </Typography>
      </Box>

      <ViewStatementDialog open={openViewDialog} documentToView={documentToView} onCancel={onCancelView} />

      {/* Statement Quality Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={onCancelDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Statement Quality Details</DialogTitle>
        <DialogContent>
          <StatementDetailsContent
            documentId={detailsDocumentId}
            splitDocNumber={detailsSplitDocNumber}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDetailsDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Statement Confirmation Dialog */}
      <Dialog open={openRemoveDialog} onClose={handleCancelRemove}>
        <DialogTitle>Remove Statement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this statement?
            {statementToRemove && statementToRemove.document_name && (
              <>
                <br />
                <strong>Document:</strong> {decodeURIComponent(statementToRemove.document_name)}
              </>
            )}
            {statementToRemove && statementToRemove.begin_date && statementToRemove.end_date && (
              <>
                <br />
                <strong>Period:</strong> {statementToRemove.begin_date} to {statementToRemove.end_date}
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} variant="contained" color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

/**
 * Fetches and renders scan-level quality details for a single statement,
 * reusing the same ExtractedDocumentPanel component shown in the document
 * upload quality dialog for a consistent look and feel.
 */
const StatementDetailsContent = ({ documentId, splitDocNumber }) => {
  const { data, error, isLoading } = useGetDocumentQualityReport(documentId, {}, !!documentId);

  if (!documentId) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No document ID available for this statement. It may have been scanned before document tracking was added.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">Loading scan quality details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
        Failed to load quality details: {error.message || 'Unknown error'}
      </Typography>
    );
  }

  // Find the extracted document report for this split document number
  const reports = data?.extracted_document_quality_reports || {};
  const report = splitDocNumber != null ? reports[splitDocNumber] || reports[String(splitDocNumber)] : null;

  if (!report) {
    // Fall back to showing the first report if split doc number doesn't match
    const reportKeys = Object.keys(reports);
    if (reportKeys.length === 1) {
      return (
        <Box sx={{ mt: 1 }}>
          <ExtractedDocumentPanel docNumber={1} report={reports[reportKeys[0]]} showDetailedChecks />
        </Box>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Could not find quality details for split document #{splitDocNumber}.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <ExtractedDocumentPanel docNumber={splitDocNumber} report={report} showDetailedChecks />
    </Box>
  );
};

export default StatementQualityReport;